/**
 * Comprehensive task configuration form for browser automation
 * Supports all available configuration options with validation
 * Works with Anchor Browser automation provider
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronDown, ChevronRight, Monitor } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
// Badge removed - not used in simplified form
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Switch } from '@/components/ui/switch'
import type { CreateTaskRequest } from '../../types/anchor-browser'
import { api } from '../../lib/api'

// Form validation schema - simplified for Anchor Browser
const taskFormSchema = z.object({
  task: z.string().min(10, 'Task description must be at least 10 characters'),
  url: z.string().url('Must be a valid URL').or(z.literal('')).optional(),
  session_id: z.string().optional(),
  create_new_session: z.boolean().optional(),
  agent: z.enum(['openai-cua']).optional(),
  provider: z.enum(['openai']).optional(),
  model: z.enum(['gpt-4o', 'gpt-4o-mini']).optional(),
  highlight_elements: z.boolean().optional(),
  browser_viewport_width: z.number().min(320).max(3840).optional(),
  browser_viewport_height: z.number().min(240).max(2160).optional(),
  max_agent_steps: z.number().min(1).max(200).optional(),
  structured_output_json: z.string().optional(),
  secrets: z.record(z.string()).optional(),
})

type TaskFormData = z.infer<typeof taskFormSchema>

interface TaskFormProps {
  onSubmit: (data: CreateTaskRequest & { session_id?: string; create_new_session?: boolean }) => void
  isLoading?: boolean
  defaultValues?: Partial<TaskFormData>
}

const MODELS = [
  { value: 'gpt-4o', label: 'GPT-4o', description: 'Most capable, best for complex tasks' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'Faster and more cost-effective' },
]

export function TaskForm({ onSubmit, isLoading = false, defaultValues }: TaskFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Query for active sessions
  const { data: sessionsData } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.sessions.listSessions(),
    refetchInterval: 30000
  })

  const form = useForm<TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      agent: 'openai-cua',
      provider: 'openai',
      model: 'gpt-4o',
      highlight_elements: true,
      browser_viewport_width: 1280,
      browser_viewport_height: 960,
      max_agent_steps: 75,
      secrets: {},
      create_new_session: true,
      ...defaultValues,
    },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form
  const watchedValues = watch()

  const handleFormSubmit = (data: TaskFormData) => {
    // For Anchor Browser, we need the task description and session info
    const cleanedData: CreateTaskRequest & { session_id?: string; create_new_session?: boolean } = {
      task: data.task,
      session_id: data.session_id,
      create_new_session: data.create_new_session
    }

    console.log('Submitting task data:', cleanedData)
    onSubmit(cleanedData)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Basic Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Task Configuration</CardTitle>
          <CardDescription>
            Define what you want the browser automation to accomplish using Anchor Browser.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Task Description */}
          <div className="space-y-2">
            <Label htmlFor="task">Task Description *</Label>
            <Textarea
              id="task"
              placeholder="Describe what you want the browser to do (e.g., 'Go to example.com and fill out the contact form')"
              className="min-h-[100px]"
              {...register('task')}
            />
            {errors.task && (
              <p className="text-sm text-destructive">{errors.task.message}</p>
            )}
          </div>

          {/* Starting URL */}
          <div className="space-y-2">
            <Label htmlFor="url">Starting URL (optional)</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://example.com"
              {...register('url')}
            />
            {errors.url && (
              <p className="text-sm text-destructive">{errors.url.message}</p>
            )}
          </div>

          {/* Session Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="create_new_session"
                checked={watchedValues.create_new_session}
                onCheckedChange={(checked) => {
                  setValue('create_new_session', checked)
                  if (checked) {
                    setValue('session_id', undefined)
                  }
                }}
              />
              <Label htmlFor="create_new_session">Create new browser session</Label>
            </div>

            {!watchedValues.create_new_session && (
              <div className="space-y-2">
                <Label htmlFor="session_id">Use Existing Session</Label>
                <Select
                  value={watchedValues.session_id || ''}
                  onValueChange={(value) => setValue('session_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an active session" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionsData?.sessions?.filter(s => s.status === 'active').length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        No active sessions available. Create a new session instead.
                      </div>
                    ) : (
                      sessionsData?.sessions?.filter(s => s.status === 'active').map((session) => (
                        <SelectItem key={session.id} value={session.id}>
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            <div>
                              <div className="font-mono text-sm">{session.id}</div>
                              <div className="text-xs text-muted-foreground">
                                Created {new Date(session.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    )}
                    {(!sessionsData?.sessions?.length ||
                      !sessionsData.sessions.some(s => s.status === 'active')) && (
                      <SelectItem value="" disabled>
                        No active sessions available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {!watchedValues.create_new_session && !watchedValues.session_id && (
                  <p className="text-sm text-muted-foreground">
                    Select an active session or enable "Create new browser session"
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Advanced Options */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between">
            Advanced Options
            {showAdvanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card>
            <CardContent className="pt-6 space-y-4">
              {/* Model Selection */}
              <div className="space-y-2">
                <Label htmlFor="model">AI Model</Label>
                <Select value={watchedValues.model} onValueChange={(value) => setValue('model', value as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELS.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        <div>
                          <div className="font-medium">{model.label}</div>
                          <div className="text-xs text-muted-foreground">{model.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Browser Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="browser_viewport_width">Viewport Width</Label>
                  <Input
                    id="browser_viewport_width"
                    type="number"
                    min="320"
                    max="3840"
                    {...register('browser_viewport_width', { valueAsNumber: true })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="browser_viewport_height">Viewport Height</Label>
                  <Input
                    id="browser_viewport_height"
                    type="number"
                    min="240"
                    max="2160"
                    {...register('browser_viewport_height', { valueAsNumber: true })}
                  />
                </div>
              </div>

              {/* Other Options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="highlight_elements"
                    checked={watchedValues.highlight_elements}
                    onCheckedChange={(checked) => setValue('highlight_elements', checked)}
                  />
                  <Label htmlFor="highlight_elements">Highlight elements during automation</Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_agent_steps">Maximum Steps</Label>
                  <Input
                    id="max_agent_steps"
                    type="number"
                    min="1"
                    max="200"
                    {...register('max_agent_steps', { valueAsNumber: true })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="structured_output_json">Structured Output Schema (JSON)</Label>
                  <Textarea
                    id="structured_output_json"
                    placeholder='{"type": "object", "properties": {...}}'
                    {...register('structured_output_json')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating Task...' : 'Create Task'}
        </Button>
      </div>
    </form>
  )
}
